import type {
  CheckoutFragment,
  MoneyFragment,
  MyOrderDetailsQuery,
  MyOrderFragment,
  OrderFragment,
} from '@/graphql/saleor/api';
import { VOUCHER_TYPE } from '@/lib/const';
import type { PreOrderData } from '@/lib/preOrder';

export type OrderDataFragment =
  | Partial<OrderFragment>
  | (MyOrderFragment & MyOrderDetailsQuery['order']);

export const formatCheckoutOrder = (
  fragment: Partial<CheckoutFragment> | OrderDataFragment | null,
) => {
  return {
    subtotal:
      (fragment as CheckoutFragment)?.subtotalPrice?.net ||
      (fragment as OrderDataFragment).subtotal?.net,
    subtotalGross:
      (fragment as CheckoutFragment).subtotalPrice?.gross ||
      (fragment as OrderDataFragment).subtotal?.gross,
    shippingPrice: fragment?.shippingPrice?.net,
    grossTotalPrice:
      (fragment as CheckoutFragment).totalPrice?.gross ||
      (fragment as OrderDataFragment).total?.gross,
    netTotalPrice:
      (fragment as CheckoutFragment).totalPrice?.net ||
      (fragment as OrderDataFragment).total?.net,
    discount:
      (fragment as CheckoutFragment).discount ||
      (fragment as OrderDataFragment).discounts?.[0]?.amount,
    totalAuthorized: (fragment as OrderDataFragment).totalAuthorized,
  };
};

export const calculateSubtotal = (
  subtotal: MoneyFragment,
  discount?: MoneyFragment,
  voucherType?: string,
): MoneyFragment => {
  if (
    !discount?.amount ||
    discount.amount <= 0 ||
    voucherType === VOUCHER_TYPE.PERCENTAGE
  )
    return subtotal;

  return {
    amount: subtotal.amount + discount.amount,
    currency: subtotal.currency,
  };
};

export const getReverseDiscount = (discount: MoneyFragment): MoneyFragment => {
  return {
    ...discount,
    amount: -discount.amount,
  };
};

export const getDepositPrice = (
  fragment?: Partial<CheckoutFragment> | OrderDataFragment | null,
  preOrderData?: PreOrderData | null,
) => {
  return {
    amount: preOrderData?.deposit,
    currency: isOrder(fragment)
      ? fragment?.total?.currency
      : fragment?.totalPrice?.currency,
  } as MoneyFragment;
};

export const getCheckoutToPayLater = (
  checkout?: Partial<CheckoutFragment> | null,
  preOrderData?: PreOrderData | null,
) => {
  return {
    ...checkout?.totalBalance,
    amount:
      (checkout?.totalPrice?.gross?.amount ?? 0) - (preOrderData?.deposit ?? 0),
  } as MoneyFragment;
};

export const getOrderToPayLater = (
  order?: Partial<OrderFragment | OrderDataFragment>,
  preOrderData?: PreOrderData | null,
) => {
  // Is used only when the order is updated with the new totalAuthorized and/or totalCharged values
  const calculatedAmount =
    (order?.total?.gross?.amount ?? 0) -
    (order?.totalAuthorized?.amount ?? 0) -
    (order?.totalCharged?.amount ?? 0);

  // final payment is the whole "pre-order balance" amount regardless of how much was already paid by split payment
  const payLaterAmount =
    (order?.total?.gross?.amount ?? 0) - (preOrderData?.deposit ?? 0);

  const amount = preOrderData
    ? payLaterAmount
    : order?.totalCharged?.amount === 0 && order?.totalAuthorized?.amount === 0
    ? Math.abs(order?.totalBalance?.amount ?? 0)
    : calculatedAmount;

  return {
    ...order?.totalBalance,
    amount: amount,
  } as MoneyFragment;
};

export const isOrder = (
  data?: unknown,
): data is Partial<OrderFragment> | OrderDataFragment =>
  Boolean((data as Partial<OrderFragment>)?.__typename === 'Order');
